import { z } from "zod"
import { marketplaceIdSchema } from "../marketplace-id"

// ── Run readiness diagnostics (issue #114) ───────────────────────────────────
//
// `nova runs diagnose <slug>` compiles a run's ACTIVE configs into three
// readiness classes — `ready`, `business_excluded`, `preparation_defect` — and
// groups every preparation defect by a DETERMINISTIC root cause + owner (the
// highest shared layer that owns the fix: writer / category / enum / image /
// source / schema / offer / copy, with an `unknown` backstop so every counted
// defect keeps an owner). The point: a preparation defect is a compiler
// failure to fix once at the shared layer, NOT a per-SKU NOK to heal one row at
// a time. NOK is reserved for products the marketplace legitimately won't carry.

/** The shared layer that owns a preparation defect's fix. */
export const runReadinessOwnerSchema = z.enum([
  "writer",
  "category",
  "enum",
  "image",
  "source",
  "schema",
  "offer",
  // The final marketplace artifact contract owns run-wide limits/configuration.
  // These blockers have no product SKU and must never masquerade as source data.
  "platform",
  // The copy pipeline (`nova copy prepare`) owns product name/description gaps —
  // heal fills these in-loop, so they are NOT a writer rule to author (issue #114).
  "copy",
  // Catch-all: a suspect preparation defect with no mapped owner (an unmapped or
  // newly-added compiler reason, a whole-config drop, or a zero-feed-row config).
  // Guarantees every counted defect still has an owner and a next action.
  "unknown",
])
export type RunReadinessOwner = z.infer<typeof runReadinessOwnerSchema>

/** Deterministic root causes a preparation defect is grouped under. Each is
 *  derived from the feed compiler's own gap reason codes (not an LLM verdict),
 *  so the same run always classifies the same way. */
export const runReadinessRootCauseSchema = z.enum([
  "writer.variant_collision",
  "writer.missing_required",
  "category.not_leaf",
  "category.unmapped",
  "enum.no_exact_value",
  "image.required_missing",
  "image.render_excluded",
  "source.missing_fact",
  "schema.changed",
  "offer.coverage_missing",
  "platform.capacity",
  "platform.configuration",
  "platform.selection",
  // A required copy attribute (name/description) is empty/placeholder — healable by
  // `nova copy prepare`, so it is copy-owned, not `writer.missing_required`.
  "copy.missing_required",
  // Backstop: a suspect defect no other root cause claimed. Ensures the distinct
  // configs across all groups always equal `totals.preparationDefect` (issue #114).
  "unknown.unclassified",
])
export type RunReadinessRootCause = z.infer<typeof runReadinessRootCauseSchema>

export const runLevelArtifactGapSchema = z.object({
  reason: z.string(),
  owner: z.literal("platform"),
  rootCause: z.enum(["platform.capacity", "platform.configuration", "platform.selection"]),
  attrCode: z.string().nullable(),
  message: z.string(),
})
export type RunLevelArtifactGap = z.infer<typeof runLevelArtifactGapSchema>

export const runReadinessRunBlockerSchema = runLevelArtifactGapSchema.extend({
  recommendedAction: z.string(),
})
export type RunReadinessRunBlocker = z.infer<typeof runReadinessRunBlockerSchema>

/** One colliding variant in a `writer.variant_collision` group: its source
 *  (pre-translation) dimension values vs the marketplace values the writer
 *  emitted. Two members collide when their emitted values are identical (e.g.
 *  both flattened to `NO SIZE`) even though their source sizes differ. */
export const variantCollisionMemberSchema = z.object({
  simpleSku: z.string(),
  sourceValues: z.record(z.string(), z.string()),
  emittedValues: z.record(z.string(), z.string()),
})
export type VariantCollisionMember = z.infer<typeof variantCollisionMemberSchema>

export const variantCollisionEvidenceSchema = z.object({
  configSku: z.string(),
  category: z.string().nullable(),
  parentValue: z.string(),
  /** The variant-dimension attribute codes whose emitted values collided. */
  dimensionAttrs: z.array(z.string()),
  members: z.array(variantCollisionMemberSchema),
})
export type VariantCollisionEvidence = z.infer<typeof variantCollisionEvidenceSchema>

export const categoryLeafCandidateSchema = z.object({ id: z.string(), path: z.string() })
export type CategoryLeafCandidate = z.infer<typeof categoryLeafCandidateSchema>

/** A non-leaf (or unmapped) category blocker: the currently-assigned category
 *  and the candidate child leaves an operator can re-map to. */
export const categoryBlockerEvidenceSchema = z.object({
  category: z.string(),
  configSkus: z.array(z.string()),
  candidateLeaves: z.array(categoryLeafCandidateSchema),
})
export type CategoryBlockerEvidence = z.infer<typeof categoryBlockerEvidenceSchema>

/** A missing enum value. `resolution` distinguishes an exact-match absence (the
 *  emitted label is not a member of the marketplace value list) from an operator
 *  choice (the writer emitted a raw operator code where a label was required).
 *  No value is ever rounded to a neighbour — the exact member set is shown so an
 *  operator maps it deliberately. */
export const enumBlockerEvidenceSchema = z.object({
  configSku: z.string(),
  simpleSku: z.string().nullable(),
  attrCode: z.string(),
  emittedValue: z.string(),
  resolution: z.enum(["no_exact_value", "operator_code"]),
  allowedValues: z.array(z.object({ code: z.string(), label: z.string() })),
  allowedValuesTruncated: z.boolean(),
})
export type EnumBlockerEvidence = z.infer<typeof enumBlockerEvidenceSchema>

/** The store-render eligibility filter that would exclude a variant from a
 *  table-image render, in the order the renderer's SQL applies them.
 *  Constant-first (same pattern as `GAP_REASON` in verdict-reasons.ts): the
 *  predicates that evaluate these (`renderExclusionFor`, beside
 *  `listEligibleVariants` in listing-images/store-render.ts) return these
 *  members, and the schema below is derived from this object, so a new filter
 *  must be added in both places or the build fails — never a bare string. */
export const RENDER_EXCLUSION_CODE = {
  missingMirrorRow: "missing_mirror_row",
  masterCategory: "master_category",
  disabled: "disabled",
  delisted: "delisted",
  longTermUnavailable: "long_term_unavailable",
  localBan: "local_ban",
  brandNotAllowed: "brand_not_allowed",
  noMagentoId: "no_magento_id",
  countryVersionSuffix: "country_version_suffix",
} as const

export const renderExclusionCodeSchema = z.enum(RENDER_EXCLUSION_CODE)
export type RenderExclusionCode = z.infer<typeof renderExclusionCodeSchema>

/** A table-image (nutrition/size) blocker: the required slot, expected object
 *  key/URL, whether an indexed rendered image exists, and — when the render
 *  request would select zero rows — which store-render eligibility filter
 *  excluded the requested Magento id. */
export const imageBlockerEvidenceSchema = z.object({
  configSku: z.string(),
  simpleSku: z.string().nullable(),
  magentoId: z.number().int().nullable(),
  ean: z.string().nullable(),
  expectedSlot: z.string().nullable(),
  expectedObjectKey: z.string().nullable(),
  expectedUrl: z.string().nullable(),
  indexed: z.boolean(),
  renderExcludedBy: renderExclusionCodeSchema.nullable(),
  detail: z.string(),
})
export type ImageBlockerEvidence = z.infer<typeof imageBlockerEvidenceSchema>

/** One preparation-defect group: a root cause, its blast radius, the affected
 *  categories/attributes, sample SKUs, a recommended next action, and
 *  root-cause-specific evidence (only the relevant array is set). */
export const runReadinessDefectGroupSchema = z.object({
  rootCause: runReadinessRootCauseSchema,
  owner: runReadinessOwnerSchema,
  /** Distinct affected config count — the blast radius the fix would clear. */
  blastRadius: z.number().int().nonnegative(),
  affectedSimples: z.number().int().nonnegative(),
  /** Affected marketplace category codes. Writers are keyed by category, so
   *  these also name the writers to fix — no separate writer list. */
  categories: z.array(z.string()),
  attrCodes: z.array(z.string()),
  sampleSkus: z.array(z.string()),
  recommendedAction: z.string(),
  variantCollisions: z.array(variantCollisionEvidenceSchema).optional(),
  categoryBlockers: z.array(categoryBlockerEvidenceSchema).optional(),
  enumBlockers: z.array(enumBlockerEvidenceSchema).optional(),
  imageBlockers: z.array(imageBlockerEvidenceSchema).optional(),
})
export type RunReadinessDefectGroup = z.infer<typeof runReadinessDefectGroupSchema>

/** A true business exclusion (brand/price/EAN/ban/availability) — a NOK the
 *  marketplace legitimately won't carry, grouped by reason. NOT a defect. */
export const runReadinessBusinessReasonSchema = z.object({
  reason: z.string(),
  count: z.number().int().nonnegative(),
  sampleSkus: z.array(z.string()),
})
export type RunReadinessBusinessReason = z.infer<typeof runReadinessBusinessReasonSchema>

export const runReadinessReportSchema = z.object({
  slug: z.string(),
  marketplace: marketplaceIdSchema,
  generatedAt: z.string(),
  totals: z.object({
    configs: z.number().int().nonnegative(),
    ready: z.number().int().nonnegative(),
    businessExcluded: z.number().int().nonnegative(),
    preparationDefect: z.number().int().nonnegative(),
  }),
  businessExcluded: z.array(runReadinessBusinessReasonSchema),
  /** Run-wide platform blockers have no SKU and do not alter per-config totals. */
  runBlockers: z.array(runReadinessRunBlockerSchema),
  /** Preparation-defect groups, ordered by blast radius (largest first). */
  preparationDefects: z.array(runReadinessDefectGroupSchema),
})
export type RunReadinessReport = z.infer<typeof runReadinessReportSchema>
